import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useApi } from '../Components/Store/ApiContext';

export const useUnitAvailability = (from, to) => {
    const [unitBookings, setUnitBookings] = useState([]);
    const [availableUnits, setAvailableUnits] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const { getData } = useApi()

    // Fetch available units and their bookings for a date range
    const fetchUnitsAvailability = useCallback(async (fromDate, toDate) => {
        const targetFrom = fromDate || from;
        const targetTo = toDate || to;

        if (!targetFrom || !targetTo) {
            setUnitBookings([]);
            setAvailableUnits([]);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            const data = await getData(() => { }, `request/unitsAvailableWithDate?from=${targetFrom}&to=${targetTo}`);
            console.log('Units availability data:', data);

            if (data.message === "success") {
                // Store available units
                setAvailableUnits(data.results || []);

                // Extract all bookings from all units to create a comprehensive booking list
                const allBookings = [];

                // Assuming the API returns units with their booking information
                // If the API structure is different, adjust accordingly
                if (Array.isArray(data.results)) {
                    data.results.forEach(unit => {
                        // If unit has bookings property
                        if (unit?.bookings && Array.isArray(unit.bookings)) {
                            unit.bookings.forEach(booking => {
                                allBookings.push({
                                    id: booking._id,
                                    unitId: unit._id,
                                    unitCode : unit.unitCode,
                                    cost : unit.cost,
                                    costInterval : unit.costInterval,
                                    checkIn: booking.reqFrom,
                                    checkOut: booking.reqTo,
                                    status: booking.reqStatus,
                                    guestName: booking.reqClientName,
                                    nights: booking.reqNights,
                                    actualCheckOut: booking.checkOutStatus === 'early'
                                        ? booking.actualCheckOutTime
                                        : booking.checkOutStatus === 'late'
                                            ? booking.actualCheckOutTime
                                            : undefined,
                                    checkoutType: booking.checkOutStatus === 'early'
                                        ? 'early'
                                        : booking.checkOutStatus === 'late'
                                            ? 'late'
                                            : 'standard'
                                });
                            });
                        }
                    });
                }

                setUnitBookings(allBookings);
            } else {
                setError(data.message || 'Failed to fetch availability');
                setUnitBookings([]);
                setAvailableUnits([]);
            }
        } catch (error) {
            console.error('Error fetching units availability:', error);
            setError(error.message || 'Failed to fetch availability');
            setUnitBookings([]);
            setAvailableUnits([]);
        } finally {
            setIsLoading(false);
        }
    }, [from, to, getData]);

    // Auto-fetch when dates change
    useEffect(() => {
        if (!from || !to) {
            setUnitBookings([]);
            setAvailableUnits([]);
            return;
        }
        
        const fetchData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const data = await getData(() => { }, `request/unitsAvailableWithDate?from=${from}&to=${to}`);

                if (data.message === "success") {
                    setAvailableUnits(data.results || []);

                    const allBookings = [];
                    if (Array.isArray(data.results)) {
                        data.results.forEach(unit => {
                            if (unit.bookings && Array.isArray(unit.bookings)) {
                                unit.bookings.forEach(booking => {
                                    allBookings.push({
                                        id: booking._id,
                                        unitId: unit._id,
                                        checkIn: booking.reqFrom,
                                        checkOut: booking.reqTo,
                                        status: booking.reqStatus,
                                        guestName: booking.reqClientName,
                                        nights: booking.reqNights,
                                        actualCheckOut: booking.checkOutStatus === 'early'
                                            ? booking.actualCheckOutTime
                                            : booking.checkOutStatus === 'late'
                                                ? booking.actualCheckOutTime
                                                : undefined,
                                        checkoutType: booking.checkOutStatus === 'early'
                                            ? 'early'
                                            : booking.checkOutStatus === 'late'
                                                ? 'late'
                                                : 'standard'
                                    });
                                });
                            }
                        });
                    }

                    setUnitBookings(allBookings);
                } else {
                    setError(data.message || 'Failed to fetch availability');
                    setUnitBookings([]);
                    setAvailableUnits([]);
                }
            } catch (error) {
                console.error('Error fetching units availability:', error);
                setError(error.message || 'Failed to fetch availability');
                setUnitBookings([]);
                setAvailableUnits([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [from, to, getData]);

    // Refresh function
    const refreshAvailability = useCallback(() => {
        return fetchUnitsAvailability(from, to);
    }, [from, to, fetchUnitsAvailability]);

    return {
        unitBookings,
        availableUnits,
        isLoading,
        error,
        fetchUnitsAvailability,
        refreshAvailability,
    };
};

export default useUnitAvailability;
